import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { isEmpty } from 'lodash-es';

import formStyles from 'portal-base/src/base/css/form.css';
import { litStyles } from 'portal-base/src/base/css/ki-button-styles.css';
import i18n, { addNls } from 'portal-base/src/decorators/i18n';
import baseWrapperStyles from 'portal-base-graph/src/components/ki-portal-base-graph/css/ki-portal-base-graph-wrapper.css';
import KiPortalBaseGraphWrapper from 'portal-base-graph/src/components/ki-portal-base-graph/ki-portal-base-graph-wrapper';
import 'portal-base/src/components/ki-portal-base-timerange-selector/ki-portal-base-timerange-selector';
import baseNls from 'portal-base/src/locales/indexes.nls';

import { KiDsGraphWrapper } from 'ds-shared-lib/src/components/ki-ds-graph-wrapper/ki-ds-graph-wrapper';
import dsWrapperStyles from 'ds-shared-lib/src/components/ki-ds-graph-wrapper/ki-ds-graph-wrapper.css';
import dsNls from 'ds-shared-lib/src/locales/indexes.nls';
import { SystemParametersStore } from 'ds-shared-lib/src/stores/SystemParametersStore';

import { getParameters } from '../../services/parameters';
import nls from '../../locales/indexes.nls';
import pwaWrapperStyles from './ki-ds-pwa-graph-wrapper.css';
import '../ki-ds-pwa-graph/ki-ds-pwa-graph';

const NAME = 'ki-ds-pwa-graph-wrapper';

@i18n(nls)
@customElement(NAME)
export class KiDsPwaGraphWrapper extends KiDsGraphWrapper {
    _className = NAME;

    static styles = [baseWrapperStyles, litStyles, dsWrapperStyles, formStyles, pwaWrapperStyles];

    async firstUpdated() {
        // Kick off Parameters init via the shared service. It seeds a standalone
        // ApplicationConfig (resolving configReady) before the singleton init, so
        // DsGlobalStore and the timerange/settings setup below don't hang waiting
        // for a config the PWA never sets. Started before the grandparent
        // firstUpdated so the config guard runs first.
        const parametersReady = getParameters();
        // Skip KiDsGraphWrapper.firstUpdated() because it calls OrganizationSettings
        // and OrganizationStore which require authentication and trigger location.reload()
        // on 401 responses. Call the grandparent (KiPortalBaseGraphWrapper) directly.
        KiPortalBaseGraphWrapper.prototype.firstUpdated.call(this);

        try {
            this.parameters = await parametersReady;
            await this.__fixTimerangeSelector();
            this.userTimeZone = this.__settings.timeZone;
            this.__bindSettings();
            this.__setTsNamePattern();
            this._setGraphTitle();
            this.paramterTypeList = await SystemParametersStore.getParameterTypes();
            this._addDelimeterAddonFunctions();
        } catch (_err) {
            this.loadingError = true;
            this._showToast({ content: this._getLabel('errorRetrievingData'), type: 'error' });
        }
    }

    updated(changedProperties) {
        if (changedProperties.has('timeSeries')) {
            this.__filterUpdates(changedProperties);
            this.qualityBarVisible = false;
        }
        super.updated(changedProperties);
    }

    __filterAddons() {}

    __filterUpdates(changedProperties) {
        const propsToFilter = ['until', 'from', 'forceReload'];

        propsToFilter.forEach((prop) => {
            changedProperties.has(prop) && changedProperties.delete(prop);
        });
    }

    async __fixTimerangeSelector() {
        const timerange = this.__timerangeSelector;
        if (!timerange) return;

        if (timerange.updateComplete) {
            await timerange.updateComplete;
        }

        // Inject ds-shared-lib NLS
        if (timerange.i18n) {
            addNls(timerange.i18n, baseNls);
            addNls(timerange.i18n, dsNls);
            addNls(timerange.i18n, nls);
        }

        async function cleanField(selector) {
            const field = timerange.shadowRoot?.querySelector(selector);
            if (!field) return;

            if (field.updateComplete) {
                await field.updateComplete;
            }

            //remove clear button
            const clearButton = field.shadowRoot?.querySelector('[part="clear-button"]');
            if (clearButton) {
                clearButton.remove();
            }

            //remove label on text field
            const labelPart = field.shadowRoot?.querySelector('[part="label"]');
            if (labelPart) {
                labelPart.remove();
            }
        }

        await cleanField('vaadin-text-field#dateFrom');
        await cleanField('vaadin-text-field#dateTo');
    }

    _renderGraphElement() {
        return html`
            <ki-ds-pwa-graph
                style="height:100%"
                ?animation=${this.animation}
                .config=${this.config}
                .data=${this.data}
                .extraConfig=${this.extraConfig}
                ?disableZoom=${this.disableZoom}
                @editAddOn=${this._createAddOnDialog}
                @editTimeSeries=${this._createEditTimeSeriesDialog}
                ?disableRemoveTimeSeries=${this.disableRemoveTimeSeries}
                id="graph"
                ?inConfigurator=${this.inConfigurator}
                ?inDashboard=${this.inDashboard}
                ?lineMarkers=${this.lineMarkers}
                .plotLines=${this.plotLines}
                .plotBands=${this.plotBands}
                ?qualityBar=${this.qualityBar}
                ?qualityBarVisible=${this.qualityBarVisible}
                .qualityCodes=${this.qualityCodes}
                .timeSeriesLegendVisibility=${this.timeSeriesLegendVisibility}
                .yAxisConfiguration=${this.yAxisConfiguration}
                @removePlotLine=${this._removePlotLine}
                @removePlotBand=${this._removePlotBand}
                @removeTimeSeries=${this._removeTimeSeries}
                @zoom=${this._changeTimerange}
                @enableOrDisableTimeseries=${({ detail }) =>
                    this._processTimeSeriesLegendStatusChange(detail)}
            >
            </ki-ds-pwa-graph>
        `;
    }

    _renderHeader() {
        return html`
            <h1>${!isEmpty(this.timeSeries) ? (this._graphTitle ?? '') : nothing}</h1>
            ${this.timeRangeSelector
                ? html` <link
                          class="css"
                          href="assets/icons/external-min.css"
                          id="hmticons"
                          rel="stylesheet"
                      />
                      <ki-portal-base-timerange-selector
                          id="timerange"
                          slot="utilityFunctions"
                          .from=${this.from}
                          .until=${this.until}
                          @change=${this._changeTimerange}
                      ></ki-portal-base-timerange-selector>`
                : ''}
        `;
    }

    async _requestTimeSeriesValues({ params }) {
        try {
            const { config, newParams } = this.__parseTsValuesRequestObjects(params);
            const tsWithData = await this.BACKEND_STORE.getTimeseriesValues(
                config,
                newParams,
                this,
            );
            isEmpty(tsWithData) && this.__cleanUpFailedRequest();
            this.__checkMatchingResolutionToData(tsWithData);
            this._resetTimeRangeOptions(tsWithData);
            const tsWithParsedData = this.__addQualityCodeToInterceptorPoints(tsWithData);

            return tsWithParsedData;
        } catch (error) {
            console.warn(error);
            this._showToast({ content: this._getLabel('errorRetrievingData'), type: 'error' });
        }
    }

    _toggleQualityBar() {
        if (!this.graph.oneVisibleSeriesInChart && !this.qualityBarVisible) {
            this._showToast({
                content: this._getLabel('qualityBarMoreThanOneSeries'),
                type: 'info',
            });
            return;
        }
        this.qualityBarVisible = !this.qualityBarVisible;
        this.messageConfigurator({ qualityBarVisible: this.qualityBarVisible });
    }
}
